import {
  Box,
  HStack,
  Text,
  VStack,
  Divider,
  Grid,
  GridItem,
} from '@chakra-ui/react'
import {
  FiUsers,
  FiRepeat,
  FiUserX,
  FiUserCheck,
  FiUserPlus,
  FiCreditCard,
} from 'react-icons/fi'
import { BsCashStack } from 'react-icons/bs'
import { AiOutlineBank } from 'react-icons/ai'

type Props = {
  totalUsers?: number | null
  businessFlag?: boolean | null
  renewalUsers?: number | null
  renewalsPercentage?: number | null
  newUsers?: number | null
  newUsersPercentage?: number | null
  churnUsers?: number | null
  churnUsersPercentage?: number | null
  churnButRenew?: number | null
  churnButRenewPercentage?: number | null
  paymentUsers?: number | null
  paymentUsersPercentage?: number | null
  creditCardUsers?: number | null
  creditCardUsersPercentage?: number | null
  bankTransferUsers?: number | null
  bankTransferUsersPercentage?: number | null
  yearRevenue?: number | null
  monthRevenue?: number | null
  yearRange?: string | null
  monthRange?: string | null
}

export default function Summary({
  totalUsers,
  businessFlag,
  renewalUsers,
  renewalsPercentage,
  newUsers,
  newUsersPercentage,
  churnUsers,
  churnUsersPercentage,
  churnButRenew,
  churnButRenewPercentage,
  paymentUsers,
  paymentUsersPercentage,
  creditCardUsers,
  creditCardUsersPercentage,
  bankTransferUsers,
  bankTransferUsersPercentage,
  yearRevenue,
  monthRevenue,
  yearRange,
  monthRange,
}: Props) {
  const primaryUserLabel = businessFlag ? 'New user' : 'Renewal user'
  const primaryUserValue = businessFlag ? newUsers : renewalUsers
  const primaryUserPercentage = businessFlag
    ? newUsersPercentage
    : renewalsPercentage
  const PrimaryUserIcon = businessFlag ? FiUserPlus : FiRepeat

  const formatCurrency = (value: number | null | undefined): string => {
    return (value ?? 0).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
  }

  return (
    <Box bg="brand.50" borderRadius="md" px={6} py={4} mt={4}>
      <VStack spacing={4} align="flex-start">
        {businessFlag && (
          <>
            <Grid
              templateColumns="repeat(4, 1fr)"
              gap={4}
              rowGap={1}
              alignItems="center"
            >
              <HStack spacing={2}>
                <BsCashStack />
                <Text>Revenue (YTD):</Text>
              </HStack>

              <Text fontWeight="bold" color="orange.400">
                ฿{formatCurrency(yearRevenue)}
              </Text>

              <HStack spacing={2}>
                <BsCashStack />
                <Text>Revenue (MTD):</Text>
              </HStack>

              <Text fontWeight="bold" color="orange.400">
                ฿{formatCurrency(monthRevenue)}
              </Text>

              <GridItem />

              <Text fontSize="xs" color="gray.500">
                {yearRange}
              </Text>

              <GridItem />

              <Text fontSize="xs" color="gray.500">
                {monthRange}
              </Text>
            </Grid>

            <Divider borderColor="brand.200" borderWidth="1px" mt={4} />
          </>
        )}

        <HStack spacing={12} justify="flex-start">
          <HStack spacing={2}>
            <FiUsers />
            <Text>
              Total user:{' '}
              <Text as="span" fontWeight="bold" color="brand.600">
                {totalUsers ?? 0}
              </Text>
            </Text>
          </HStack>

          <HStack spacing={2}>
            <PrimaryUserIcon />
            <Text>
              {primaryUserLabel}:{' '}
              <Text as="span" fontWeight="bold" color="brand.600">
                {primaryUserValue ?? 0}
              </Text>
              <Text as="span" color="brand.600">
                &nbsp;({primaryUserPercentage ?? 0} %)
              </Text>
            </Text>
          </HStack>

          <HStack spacing={2}>
            <FiUserX />
            <Text>
              Churn user:{' '}
              <Text as="span" fontWeight="bold" color="brand.600">
                {churnUsers ?? 0}
              </Text>
              <Text as="span" color="brand.600">
                &nbsp;({churnUsersPercentage ?? 0} %)
              </Text>
            </Text>
          </HStack>

          <HStack spacing={2}>
            <FiUserCheck />
            <Text>
              Churn but Renew:{' '}
              <Text as="span" fontWeight="bold" color="brand.600">
                {churnButRenew ?? 0}
              </Text>
              <Text as="span" color="brand.600">
                &nbsp;({churnButRenewPercentage ?? 0} %)
              </Text>
            </Text>
          </HStack>
        </HStack>

        {businessFlag && (
          <HStack spacing={12}>
            <HStack spacing={2}>
              <FiUsers />
              <Text>
                Paid users:{' '}
                <Text as="span" fontWeight="bold" color="brand.600">
                  {paymentUsers ?? 0}
                </Text>
                <Text as="span" color="brand.600">
                  &nbsp;({paymentUsersPercentage ?? 0}% of total users)
                </Text>
              </Text>
            </HStack>

            <HStack spacing={2}>
              <FiCreditCard />
              <Text>
                Credit card:{' '}
                <Text as="span" fontWeight="bold" color="brand.600">
                  {creditCardUsers ?? 0}
                </Text>
                <Text as="span" color="brand.600">
                  &nbsp;({creditCardUsersPercentage ?? 0} %)
                </Text>
              </Text>
            </HStack>

            <HStack spacing={2}>
              <AiOutlineBank />
              <Text>
                Bank Transfer:{' '}
                <Text as="span" fontWeight="bold" color="brand.600">
                  {bankTransferUsers ?? 0}
                </Text>
                <Text as="span" color="brand.600">
                  &nbsp;({bankTransferUsersPercentage ?? 0} %)
                </Text>
              </Text>
            </HStack>
          </HStack>
        )}
      </VStack>
    </Box>
  )
}
